import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiUploadCloud, FiLink, FiTrash2, FiAlertTriangle, FiLoader } from 'react-icons/fi';
import { settingsAPI } from '../../services/api';
import './ImageUploadField.scss';

/**
 * Dimension rules, mirrored from the server (brandingAssetService). Checking in
 * the browser gives an instant, specific reason for a rejection; the server
 * re-checks and is the one that actually enforces it.
 */
export const IMAGE_RULES = {
  logo: {
    maxBytes: 2 * 1024 * 1024,
    min: { width: 32, height: 32 },
    max: { width: 4000, height: 4000 },
    recommended: { width: 256, height: 256 },
    maxAspect: 8,
  },
  hero: {
    maxBytes: 8 * 1024 * 1024,
    min: { width: 1280, height: 720 },
    max: { width: 8000, height: 8000 },
    recommended: { width: 1920, height: 1080 },
    maxAspect: 4,
  },
};

const ACCEPT = 'image/png,image/jpeg,image/webp,image/gif,image/svg+xml';

/** Read pixel dimensions client-side so an unusable file never leaves the browser. */
function measure(file) {
  return new Promise((resolve) => {
    // SVG has no fixed pixel size — let the server be the judge.
    if (file.type === 'image/svg+xml') { resolve({ vector: true, width: 0, height: 0 }); return; }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });
}

/**
 * Logo / hero picker for the Branding Studio.
 *
 * Uploading is the primary path, but the URL box stays available and pre-filled:
 * tenants already pointing at an externally hosted image keep that value and can
 * keep editing it by hand.
 */
function ImageUploadField({ kind, value, onChange, disabled, help }) {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [showUrl, setShowUrl] = useState(false);
  const [previewBroken, setPreviewBroken] = useState(false);

  const rules = IMAGE_RULES[kind] || IMAGE_RULES.logo;
  const recommendation = useMemo(
    () => `${rules.recommended.width}×${rules.recommended.height}px`,
    [rules]
  );

  const reject = useCallback((code, detail) => {
    setWarning(null);
    setError(t(`imageUpload.errors.${code}`, { ...detail, defaultValue: t('imageUpload.errors.generic') }));
  }, [t]);

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    setError(null);
    setWarning(null);

    if (!ACCEPT.split(',').includes(file.type)) {
      reject('unsupportedType', { type: file.type || '—' });
      return;
    }
    if (file.size > rules.maxBytes) {
      reject('tooLarge', {
        maxMb: Math.round(rules.maxBytes / (1024 * 1024)),
        actualMb: (file.size / (1024 * 1024)).toFixed(1),
      });
      return;
    }

    const meta = await measure(file);
    if (!meta) { reject('unreadable', {}); return; }

    if (!meta.vector) {
      const { width, height } = meta;
      if (width < rules.min.width || height < rules.min.height) {
        reject('tooSmall', {
          width, height, minWidth: rules.min.width, minHeight: rules.min.height,
        });
        return;
      }
      if (width > rules.max.width || height > rules.max.height) {
        reject('tooBig', {
          width, height, maxWidth: rules.max.width, maxHeight: rules.max.height,
        });
        return;
      }
      const aspect = Math.max(width / height, height / width);
      if (aspect > rules.maxAspect) {
        reject('badAspect', { width, height, maxAspect: rules.maxAspect });
        return;
      }
    }

    setBusy(true);
    try {
      const { data } = await settingsAPI.uploadBrandingImage(kind, file);
      setPreviewBroken(false);
      onChange(data.url);
      if (data.warnings?.includes('belowRecommended')) {
        setWarning(t('imageUpload.warnings.belowRecommended', {
          width: data.width, height: data.height, recommended: recommendation,
        }));
      }
    } catch (err) {
      const body = err.response?.data;
      if (body?.code) reject(body.code, body.detail || {});
      else setError(body?.error || t('imageUpload.errors.generic'));
    } finally {
      setBusy(false);
    }
  }, [kind, onChange, recommendation, reject, rules, t]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (disabled || busy) return;
    handleFile(e.dataTransfer.files?.[0]);
  };

  const clear = () => {
    setError(null);
    setWarning(null);
    setPreviewBroken(false);
    onChange('');
  };

  const hasValue = typeof value === 'string' && value.trim().length > 0;

  return (
    <div className="imgfield">
      {hasValue ? (
        <div className={`imgfield__current imgfield__current--${kind}`}>
          <div className="imgfield__thumb">
            {previewBroken
              ? <span className="imgfield__thumb-fallback"><FiAlertTriangle /></span>
              : <img src={value} alt="" onError={() => setPreviewBroken(true)} />}
          </div>
          <div className="imgfield__meta">
            <span className="imgfield__filename" title={value}>{value.split('/').pop()}</span>
            {previewBroken && (
              <span className="imgfield__meta-warn">{t('imageUpload.previewFailed')}</span>
            )}
            <div className="imgfield__meta-actions">
              <button type="button" onClick={() => inputRef.current?.click()} disabled={disabled || busy}>
                {t('imageUpload.replace')}
              </button>
              <button type="button" onClick={() => setShowUrl((v) => !v)}>
                {t('imageUpload.editUrl')}
              </button>
            </div>
          </div>
          <button
            type="button"
            className="imgfield__remove"
            onClick={clear}
            disabled={disabled || busy}
            aria-label={t('imageUpload.remove')}
            title={t('imageUpload.remove')}
          >
            <FiTrash2 />
          </button>
        </div>
      ) : (
        <button
          type="button"
          className={`imgfield__drop ${dragging ? 'imgfield__drop--over' : ''}`}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          disabled={disabled || busy}
        >
          <span className="imgfield__drop-icon">{busy ? <FiLoader className="imgfield__spin" /> : <FiUploadCloud />}</span>
          <span className="imgfield__drop-title">
            {busy ? t('imageUpload.uploading') : t('imageUpload.cta')}
          </span>
          <span className="imgfield__drop-hint">
            {t('imageUpload.hint', { recommended: recommendation, maxMb: Math.round(rules.maxBytes / (1024 * 1024)) })}
          </span>
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        hidden
        onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }}
      />

      {!hasValue && (
        <button type="button" className="imgfield__urltoggle" onClick={() => setShowUrl((v) => !v)}>
          <FiLink /> {showUrl ? t('imageUpload.hideUrl') : t('imageUpload.useUrl')}
        </button>
      )}

      {showUrl && (
        <input
          className="studio-input imgfield__url"
          value={value || ''}
          onChange={(e) => { setPreviewBroken(false); onChange(e.target.value); }}
          placeholder={kind === 'logo' ? 'https://yourcompany.com/logo.png' : 'https://yourcompany.com/hero.jpg'}
          disabled={disabled}
        />
      )}

      {error && <span className="imgfield__error"><FiAlertTriangle /> {error}</span>}
      {warning && !error && <span className="imgfield__warn"><FiAlertTriangle /> {warning}</span>}
      {help && !error && <span className="studio-field__help">{help}</span>}
    </div>
  );
}

export default ImageUploadField;
